import { useMemo } from 'react'
import { Download, Terminal } from 'lucide-react'
import { Badge, Card, PageHeader } from '../components/ui'
import { cn } from '../lib/cn'

interface DownloadOption {
  label: string
  file: string
  os: 'mac' | 'windows' | 'linux'
}

const DOWNLOADS: DownloadOption[] = [
  { label: 'macOS (Apple Silicon + Intel, universal)', file: 'idefy-darwin-universal', os: 'mac' },
  { label: 'Windows 10/11 (x86_64)', file: 'idefy-windows-amd64.exe', os: 'windows' },
  { label: 'Linux (x86_64)', file: 'idefy-linux-amd64', os: 'linux' },
]

// Smaller, single-architecture macOS builds — most users want the universal
// binary above; these are tucked away for anyone who cares about download size.
const MAC_ARCH_DOWNLOADS: DownloadOption[] = [
  { label: 'macOS (Apple Silicon only)', file: 'idefy-darwin-arm64', os: 'mac' },
  { label: 'macOS (Intel only)', file: 'idefy-darwin-amd64', os: 'mac' },
]

function detectOS(): 'mac' | 'windows' | 'linux' | null {
  const ua = navigator.userAgent
  if (ua.includes('Mac')) return 'mac'
  if (ua.includes('Win')) return 'windows'
  if (ua.includes('Linux')) return 'linux'
  return null
}

export default function NativeAccessSetupPage() {
  const detected = useMemo(detectOS, [])
  const portalOrigin = window.location.origin

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Native Access Setup"
        description={
          <>
            Install the <code className="text-foreground">idefy</code> CLI to use native{' '}
            <code className="text-foreground">ssh</code> against resources in this tenant&apos;s
            private network.
          </>
        }
      />

      <div className="space-y-4">
        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">1</span>
            <h2 className="text-sm font-semibold text-foreground">Download</h2>
            <Download className="h-4 w-4 text-muted" />
          </div>
          <div className="space-y-2">
            {DOWNLOADS.map((d) => (
              <a
                key={d.file}
                href={`/downloads/${d.file}`}
                className={cn(
                  'flex items-center justify-between rounded-lg border px-3 py-3 transition-colors',
                  detected === d.os
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-surface-muted',
                )}
              >
                <span className="text-sm text-foreground">
                  {d.label}
                  {detected === d.os && (
                    <Badge variant="primary" className="ml-2">detected</Badge>
                  )}
                </span>
                <span className="font-mono text-xs text-muted">{d.file}</span>
              </a>
            ))}
          </div>

          <details className="mt-2 group">
            <summary className="cursor-pointer select-none text-xs font-medium text-muted hover:text-foreground">
              Other macOS downloads (smaller, single-architecture)
            </summary>
            <div className="mt-2 space-y-2">
              {MAC_ARCH_DOWNLOADS.map((d) => (
                <a
                  key={d.file}
                  href={`/downloads/${d.file}`}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-3 transition-colors hover:border-primary/40 hover:bg-surface-muted"
                >
                  <span className="text-sm text-foreground">{d.label}</span>
                  <span className="font-mono text-xs text-muted">{d.file}</span>
                </a>
              ))}
            </div>
          </details>

          <div className="mt-3 space-y-2 text-xs text-muted">
            <p>
              <strong className="text-foreground">macOS:</strong> run{' '}
              <code>chmod +x idefy-darwin-*</code>, rename it to <code>idefy</code>, and move it
              onto your <code>PATH</code> (e.g.{' '}
              <code>mv idefy-darwin-universal /usr/local/bin/idefy</code>) so the commands below
              work as shown. First run may need{' '}
              <code>xattr -d com.apple.quarantine idefy-darwin-*</code> or right-click → Open.
            </p>
            <p>
              <strong className="text-foreground">Linux:</strong> run{' '}
              <code>chmod +x idefy-linux-amd64</code>, rename it to <code>idefy</code>, and move
              it onto your <code>PATH</code> (e.g.{' '}
              <code>sudo mv idefy-linux-amd64 /usr/local/bin/idefy</code>) so the commands below
              work as shown.
            </p>
            <p>
              <strong className="text-foreground">Windows:</strong> rename{' '}
              <code>idefy-windows-amd64.exe</code> to <code>idefy.exe</code> and place it in a
              folder on your <code>PATH</code> (or run it from the download folder using its full
              filename instead of <code>idefy</code> in the commands below).
            </p>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">2</span>
            <h2 className="text-sm font-semibold text-foreground">Log in</h2>
          </div>
          <p className="mb-3 text-xs text-muted">
            Opens your browser for a normal login against your identity provider — including MFA if required.
          </p>
          <pre className="overflow-x-auto rounded-lg border border-border bg-surface-muted p-3 font-mono text-xs text-primary">
            idefy login --identity-url {portalOrigin}
          </pre>
        </Card>

        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">3</span>
            <h2 className="text-sm font-semibold text-foreground">Install SSH config</h2>
            <Terminal className="h-4 w-4 text-muted" />
          </div>
          <p className="mb-3 text-xs text-muted">
            Writes a managed block into <code>~/.ssh/config</code> so SSH to your tenant&apos;s private
            network ranges is routed through the Idefy bastion.
          </p>
          <pre className="overflow-x-auto rounded-lg border border-border bg-surface-muted p-3 font-mono text-xs text-primary">
            idefy ssh config install
          </pre>
          <p className="mt-3 text-xs text-muted">
            Then use <code>ssh &lt;host&gt;</code> for any host in your tenant&apos;s configured ranges.
          </p>
        </Card>
      </div>
    </div>
  )
}
